<template>
  <div>
    <div class="text-right expand-collapse">
      <b-link class="text-secondary" @click="expandAll">Expand All</b-link> |
      <b-link class="text-secondary" @click="collapseAll">Collapse All</b-link>
    </div>
    <ServiceDetailsCard
      v-if="!isServiceEdit"
      ref="serviceDetailsCard"
    />
    <ServiceForm
      v-if="isServiceEdit"
      ref="editServiceCard"
    />
    <InspectionDetailsCard
      v-if="isInspectionService"
      ref="inspectionDetailsCard"
    />
    <AssociatedPropertiesCard
      :storeNamespace="propertiesStoreNamespace"
      :parentNamespace="'Service'"
      :canManageProperties="canManageProperties"
      ref="associatedPropertiesCard"
    />
    <ContactsCard
      :parentId="currentService.id"
      :parentType="'service'"
      :parentTitle="'Service'"
      :roles="contactRoles"
      :rolesLoading="rolesLoading"
      :entities="contacts"
      :entitiesLoading="contactsLoading"
      ref="contactsCard"
    />
    <ReviewCard
      :parentId="currentService.id"
      :parentType="'service'"
      :parentTitle="'Service'"
      :canAssignReviews="true"
      :reviewStatuses="reviewStatuses"
      :reviewStatusesLoading="reviewStatusesLoading"
      :entities="reviews"
      :entitiesLoading="reviewsLoading"
      ref="reviewCard"
    />
    <EventCard
      :parentId="currentService.id"
      :parentType="'service'"
      :parentTitle="'Service'"
      :eventTypes="eventTypes"
      :eventTypesLoading="eventTypesLoading"
      :entities="events"
      :entitiesLoading="eventsLoading"
      ref="eventCard"
    />
    <BoxCard
      storeNamespace="Service"
      :folderId="currentService.documentFolderId"
      :parentId="currentService.id"
      ref="boxCard"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import InspectionDetailsCard from '@/components/service/InspectionDetailsCard.vue';
import AssociatedPropertiesCard from '@/components/associatedProperties/AssociatedPropertiesCard.vue';
import ContactsCard from '@/components/contacts/ContactsCard.vue';
import ServiceDetailsCard from '@/components/service/ServiceDetailsCard.vue';
import ServiceForm from '@/components/service/ServiceForm.vue';
import { LookupModules, EntityModules } from '@/store/moduleConfig';
import RouterCheck from '@/mixins/RouterCheck';
import ReviewCard from '../components/reviews/ReviewCard.vue';
import EventCard from '../components/event/EventCard.vue';
import BoxCard from '../components/common/BoxCard.vue';

export default {
  name: 'ProjectService',
  mixins: [RouterCheck],
  components: {
    InspectionDetailsCard,
    AssociatedPropertiesCard,
    BoxCard,
    ContactsCard,
    EventCard,
    ReviewCard,
    ServiceDetailsCard,
    ServiceForm,
  },
  data() {
    return {
      propertiesStoreNamespace: EntityModules.SERVICE_PROPERTIES.namespace,
    };
  },
  created() {
    this.dispatchFetch();
    this.$store.dispatch(`${LookupModules.SERVICE_EVENT_TYPES.namespace}/fetchOptions`,
      LookupModules.SERVICE_EVENT_TYPES.url);
    this.$store.dispatch(`${LookupModules.REVIEW_STATUSES.namespace}/fetchOptions`,
      LookupModules.REVIEW_STATUSES.url);
    this.$store.dispatch(`${LookupModules.SERVICE_ROLES.namespace}/fetchOptions`,
      LookupModules.SERVICE_ROLES.url);
  },
  computed: {
    ...mapState('Service', {
      currentService: state => state.currentService,
      isServiceEdit: state => state.isServiceEdit,
    }),
    ...mapState(EntityModules.SERVICE_EVENT.namespace, {
      events: state => state.entities,
      eventsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SERVICE_EVENT_TYPES.namespace, {
      eventTypesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.REVIEW_STATUSES.namespace, {
      reviewStatusesLoading: state => state.loading,
    }),
    ...mapState(EntityModules.SERVICE_REVIEW.namespace, {
      reviews: state => state.entities,
      reviewsLoading: state => state.loading,
    }),
    ...mapState(EntityModules.SERVICE_CONTACT.namespace, {
      contacts: state => state.entities,
      contactsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SERVICE_ROLES.namespace, {
      rolesLoading: state => state.loading,
    }),
    ...mapGetters(LookupModules.SERVICE_EVENT_TYPES.namespace, {
      getEventTypeName: 'getName',
      eventTypes: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.REVIEW_STATUSES.namespace, {
      reviewStatuses: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.SERVICE_ROLES.namespace, {
      contactRoles: 'getActiveOptions',
    }),
    isInspectionService() {
      return this.currentService.categoryId === 1;
    },
    canManageProperties() {
      return (this.$store.state[EntityModules.SERVICE_PROPERTIES.namespace].entities
        && this.$store.state[EntityModules.SERVICE_PROPERTIES.namespace].entities.length > 1);
    },
  },
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('Service/clearCurrentService');
    this.$store.dispatch('Service/stopEditing');
    next();
  },
  watch: {
    $route: 'dispatchFetch',
  },
  methods: {
    getCardRefs() {
      const refs = [
        this.$refs.associatedPropertiesCard,
        this.$refs.contactsCard,
        this.$refs.reviewCard,
        this.$refs.eventCard,
        this.$refs.boxCard,
      ];
      if (!this.isServiceEdit) {
        refs.push(this.$refs.serviceDetailsCard);
      }
      if (this.isInspectionService) {
        refs.push(this.$refs.inspectionDetailsCard);
      }
      return refs;
    },
    expandAll() {
      this.getCardRefs().forEach((ref) => {
        ref.forceExpand();
      });
    },
    collapseAll() {
      this.getCardRefs().forEach((ref) => {
        ref.forceCollapse();
      });
    },
    dispatchFetch() {
      this.$store.dispatch('Service/fetchService', {
        projectId: parseInt(this.$route.params.project_id, 10),
        id: parseInt(this.$route.params.id, 10),
      }).then(() => {
        this.$store.dispatch(`${EntityModules.SERVICE_PROPERTIES.namespace}/fetchEntities`, {
          parentType: EntityModules.SERVICE_PROPERTIES.parentType,
          parentId: parseInt(this.$route.params.id, 10),
          entityType: EntityModules.SERVICE_PROPERTIES.entityType,
        });
        this.$store.dispatch(`${EntityModules.SERVICE_CONTACT.namespace}/fetchEntities`, {
          parentType: EntityModules.SERVICE_CONTACT.parentType,
          parentId: this.currentService.id,
          entityType: EntityModules.SERVICE_CONTACT.entityType,
        });
        this.$store.dispatch(`${EntityModules.SERVICE_REVIEW.namespace}/fetchEntities`, {
          parentType: EntityModules.SERVICE_REVIEW.parentType,
          parentId: this.currentService.id,
          entityType: EntityModules.SERVICE_REVIEW.entityType,
        });
        this.$store.dispatch(`${EntityModules.SERVICE_EVENT.namespace}/fetchEntities`, {
          parentType: EntityModules.SERVICE_EVENT.parentType,
          parentId: this.currentService.id,
          entityType: EntityModules.SERVICE_EVENT.entityType,
        });
      });
    },
  },
};
</script>
